import {
  RestaurantSettingKey,
  type RestaurantSetting,
} from "@lemmonhq/types";

export const MOBILE_POS_ORDER_TYPE_VALUES = [
  "table",
  "dine-in",
  "takeaway",
] as const;

export type MobilePosOrderType = (typeof MOBILE_POS_ORDER_TYPE_VALUES)[number];

export type MobilePosOrderTypeOption = {
  value: MobilePosOrderType;
  labelKey: string;
  icon: string;
  btnClass: string;
};

export const MOBILE_POS_ORDER_TYPE_OPTIONS: MobilePosOrderTypeOption[] = [
  {
    value: "table",
    labelKey: "general.words.table_order",
    icon: "table_restaurant",
    btnClass: "btn-primary",
  },
  {
    value: "dine-in",
    labelKey: "general.words.pay",
    icon: "local_shipping",
    btnClass: "btn-accent",
  },
  {
    value: "takeaway",
    labelKey: "general.words.takeaway",
    icon: "takeout_dining",
    btnClass: "btn-secondary",
  },
];

export const ALL_MOBILE_POS_ORDER_TYPES: MobilePosOrderType[] = [
  ...MOBILE_POS_ORDER_TYPE_VALUES,
];

const isMobilePosOrderType = (value: unknown): value is MobilePosOrderType =>
  typeof value === "string" &&
  MOBILE_POS_ORDER_TYPE_VALUES.includes(value as MobilePosOrderType);

const parseRawMobilePosOrderTypes = (value: unknown): unknown => {
  if (typeof value === "string" && value.trim().length > 0) {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error("Failed to parse mobile POS order types", value, error);
      return null;
    }
  }

  return value;
};

export const parseMobilePosOrderTypes = (
  value: unknown,
): MobilePosOrderType[] => {
  const parsed = parseRawMobilePosOrderTypes(value);
  if (!Array.isArray(parsed)) return [];

  const selectedOrderTypes = new Set(parsed.filter(isMobilePosOrderType));
  return ALL_MOBILE_POS_ORDER_TYPES.filter((orderType) =>
    selectedOrderTypes.has(orderType),
  );
};

export const getEffectiveMobilePosOrderTypesFromSettings = (
  settings: RestaurantSetting[] | null | undefined,
): MobilePosOrderType[] => {
  const raw = settings?.find(
    (setting) => setting.key === RestaurantSettingKey.MOBILE_POS_ORDER_TYPES,
  )?.value;
  const parsed = parseRawMobilePosOrderTypes(raw);

  if (parsed == null) return [...ALL_MOBILE_POS_ORDER_TYPES];
  const orderTypes = parseMobilePosOrderTypes(parsed);

  return orderTypes.length > 0 ? orderTypes : [...ALL_MOBILE_POS_ORDER_TYPES];
};

export const getMobilePosOrderTypeOptions = (
  orderTypes: MobilePosOrderType[],
) =>
  MOBILE_POS_ORDER_TYPE_OPTIONS.filter((option) =>
    orderTypes.includes(option.value),
  );
